import React from 'react';

import styled from 'styled-components';
import { Handle, NodeResizer, Position } from 'reactflow';

import { CustomNodeProps, GroupBlockData } from 'types';
import useNodeResize from './hooks/useNodeResize';
import GroupNodeHeader from './components/GroupNodeHeader';
import useGroupNodeExpand from './hooks/useGroupNodeExpand';
import useHandlePosition from '../../../hooks/useHandlePosition';

const Wrapper = styled.div<{ $isSelected: boolean }>`
  .react-flow__resize-control.line {
    border-color: ${(props) => (props.$isSelected ? props.theme.global.mainColor : props.theme.global.borderColor)};
  }

  .react-flow__resize-control.handle {
    ${(props) =>
      !props.$isSelected && {
        display: 'none',
      }}
  }
`;

const Container = styled.div<{ $isSelected: boolean; $isExpand?: boolean; $totalSource: number; $totalTarget: number }>`
  position: relative;
  height: 100%;
  border-radius: 4px;

  ${(props) =>
    !props.$isExpand && {
      outline: props.$isSelected ? `2px solid ${props.theme.global.mainColor}` : 'none',
      border: `1.5px solid ${props.theme.global.borderColor}`,
    }}

  .source-handle,
  .target-handle {
    opacity: 1;
    width: 12px;
    height: 12px;
    border: 1px solid ${(props) => (props.$isSelected ? props.theme.global.mainColor : '#808080')};
    background-color: #ffffff;
  }

  .source-handle.right {
    right: 0;
    transform: translate(50%, -50%);
  }
  .source-handle.bottom {
    bottom: 0;
    transform: translate(-50%, 50%);
  }

  .target-handle.top {
    top: 0;
    transform: translate(-50%, -50%);
  }
  .target-handle.left {
    bottom: 0;
    transform: translate(-50%, -50%);
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background-color: 'transparent';
  padding: 8px 12px;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
  height: 100%;
  background-color: ${(props) => props.theme.global.bgColor};
`;

const Block = styled.div<{ $color: string; $expand?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: ${(props) => (props.$expand ? 'initial' : '300px')};
  font-size: 14px;
  font-weight: 500;
  height: 100%;
  border-radius: 4px;
`;

const GroupNode = ({ id, selected, data, modelDirection }: CustomNodeProps<GroupBlockData>) => {
  const { expand, sourceNodeIds, targetNodeIds } = data;
  const { minWidth, minHeight, onResizeStart } = useNodeResize({ id });
  const { handlePosition } = useHandlePosition(modelDirection);
  const { onExpandClick } = useGroupNodeExpand({ id, data });

  return (
    <Wrapper $isSelected={selected}>
      {expand && (
        <NodeResizer
          handleStyle={{ height: '8px', width: '8px' }}
          minHeight={minHeight}
          minWidth={minWidth}
          onResizeStart={onResizeStart}
        />
      )}
      <Container
        $isSelected={selected}
        $isExpand={expand}
        $totalSource={sourceNodeIds.length}
        $totalTarget={targetNodeIds.length}
      >
        <Block $color={`#909090`} $expand={expand}>
          <GroupNodeHeader id={id} data={data} isSelected={selected} onExpandClick={onExpandClick} />
          {!expand && <Body></Body>}
          {!expand &&
            sourceNodeIds.map((sourceId: string, i: number) => (
              <Handle
                key={`source${id}${sourceId}`}
                className={`source-handle ${handlePosition.source}`}
                type="source"
                position={handlePosition.source}
                id={`${sourceId}_source`}
                style={{
                  left:
                    handlePosition.source === Position.Right
                      ? 'initial'
                      : `${(100 / (sourceNodeIds.length + 1)) * (i + 1)}%`,
                  top:
                    handlePosition.source === Position.Right
                      ? `${(100 / (sourceNodeIds.length + 1)) * (i + 1)}%`
                      : 'initial',
                }}
              />
            ))}
          {!expand &&
            targetNodeIds.map((targetId: string, i: number) => (
              <Handle
                key={`target${id}${targetId}`}
                className={`target-handle ${handlePosition.target}`}
                type="target"
                position={handlePosition.target}
                id={`${targetId}_target`}
                style={{
                  left:
                    handlePosition.source === Position.Left ? 0 : `${(100 / (targetNodeIds.length + 1)) * (i + 1)}%`,
                  top: handlePosition.source === Position.Left ? `${(100 / (targetNodeIds.length + 1)) * (i + 1)}%` : 0,
                }}
              />
            ))}
        </Block>
      </Container>
    </Wrapper>
  );
};

export default React.memo(GroupNode);
